import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Flex from 'antd/lib/flex';
import Form from 'antd/lib/form';
import { useForm } from 'antd/lib/form/Form';
import Input from 'antd/lib/input';
import Modal from 'antd/lib/modal';
import Typography from 'antd/lib/typography';
import { omit } from 'lodash';
import React, { useState } from 'react';
import { DisplayedFilterFieldAndValue } from 'src/components/sidebar-filters/saved-filter/DisplayedFilterFieldAndValue';
import { ICreateSavedFilter } from 'src/components/sidebar-filters/saved-filter/interface';
import { BILL_FILTER_LABELS, TBillFilterKey, billFilterValueToDisplay, cleanBillFilterValues } from './billSavedFilters';
import { IBillSidebarFilters } from './billSidebarFilters';

interface ISaveCustomerUtilityBillFiltersModalProps {
    isLoading: boolean;
    initialSavedFilter: ICreateSavedFilter;
    onCancel: () => void;
    onSave: (filter: ICreateSavedFilter) => Promise<void>;
}

interface ISaveBillFiltersFormValues {
    filter_name: string;
    description?: string;

    id: number | null;
    status: string;
    type: string;
    sub_type: string;
    display_in_dashboard?: boolean;
}

export const SaveCustomerUtilityBillFiltersModal: React.FC<ISaveCustomerUtilityBillFiltersModalProps> = ({
    initialSavedFilter,
    onSave,
    onCancel,
    isLoading,
}) => {
    const { notification } = App.useApp();
    const [form] = useForm<ISaveBillFiltersFormValues>();
    const [savedFilterValues, setSavedFilterValues] = useState<Partial<IBillSidebarFilters>>(
        cleanBillFilterValues(initialSavedFilter?.filter_values as IBillSidebarFilters)
    );

    const handleRemoveFilterProperty = (filterKey: string) => {
        if (Object.keys(savedFilterValues).length <= 1) {
            notification.warning({ key: 'unable-remove-filter-property', message: 'You must have at least one filter selected!' });
            return;
        }

        setSavedFilterValues(prev => omit(prev, filterKey));
    };

    const handleFormSubmit = async (filterData: ISaveBillFiltersFormValues) => {
        await onSave({
            ...filterData,
            filter_values: savedFilterValues,
        });
    };

    const displayedFilterEntries = Object.entries(savedFilterValues) as [TBillFilterKey, unknown][];

    return (
        <Modal
            open
            destroyOnHidden
            title={initialSavedFilter.id ? 'Edit Filter Selections' : 'Save Filter Selections'}
            onCancel={() => onCancel()}
            footer={[
                <Button key="modal-cancel" onClick={() => onCancel()} loading={isLoading}>
                    Cancel
                </Button>,
                <Button type="primary" key="modal-save" onClick={() => form.submit()} loading={isLoading}>
                    Save
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical" preserve={false} initialValues={initialSavedFilter || {}} onFinish={handleFormSubmit}>
                <Form.Item hidden name="id">
                    <Input />
                </Form.Item>
                <Form.Item hidden name="status">
                    <Input />
                </Form.Item>
                <Form.Item hidden name="type">
                    <Input />
                </Form.Item>
                <Form.Item hidden name="sub_type">
                    <Input />
                </Form.Item>
                <Form.Item hidden name="display_in_dashboard">
                    <Input />
                </Form.Item>

                <Form.Item
                    name="filter_name"
                    label={<Typography.Text strong>Name</Typography.Text>}
                    rules={[
                        { required: true, message: 'Please enter name!' },
                        { max: 100, message: 'Number of characters should be less than 100' },
                    ]}
                >
                    <Input placeholder="Filter Name" size="large" />
                </Form.Item>
                <Form.Item
                    name="description"
                    label={<Typography.Text strong>Description</Typography.Text>}
                    rules={[{ max: 255, message: 'Number of characters should be less than 255' }]}
                >
                    <Input.TextArea placeholder="Description" size="large" autoSize={{ minRows: 2, maxRows: 6 }} />
                </Form.Item>

                <Flex vertical gap={12}>
                    <Typography.Text strong>Selected Filters</Typography.Text>

                    {displayedFilterEntries.length === 0 && <Typography.Text>-</Typography.Text>}

                    <Flex wrap="wrap" gap={8}>
                        {displayedFilterEntries.map(([key, value]) => (
                            <DisplayedFilterFieldAndValue
                                key={key}
                                fieldName={BILL_FILTER_LABELS[key]}
                                fieldValue={billFilterValueToDisplay(key, value)}
                                isFieldOutdated={false}
                                onClose={() => handleRemoveFilterProperty(key)}
                            />
                        ))}
                    </Flex>
                </Flex>
            </Form>
        </Modal>
    );
};
